import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd,
  Psychology,
  Security,
  ArrowBack
} from '@mui/icons-material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../contexts/AuthContext';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import SocialLoginButtons from './SocialLoginButtons';
import api from '../api/client';
import AuthModeTabs from './auth/AuthModeTabs';
import { AUTH_PAGE_BG_SX } from './auth/authPageSx';

/** Login: single "Continue with Google" (Option A). Full consent gate lives on Register. */
const Login = ({ onSuccess, switchToRegister, onBack, postAuthRedirect = '/dashboard' }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [regDocuments, setRegDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);

  const { login } = useAuth();
  const recaptchaRef = useRef();

  useEffect(() => {
    setDocsLoading(true);
    api.legal.getFlowDocuments('registration')
      .then(({ data }) => setRegDocuments(data.documents || []))
      .catch(() => setRegDocuments([]))
      .finally(() => setDocsLoading(false));
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check if CAPTCHA is required but not provided
    if (captchaRequired && !captchaToken) {
      setError('Please complete the CAPTCHA verification');
      setLoading(false);
      return;
    }

    try {
      // Include device fingerprint for enhanced rate limiting
      const deviceFingerprint = getDeviceFingerprint();
      
      // Include CAPTCHA token in login request if available
      const loginData = {
        email: formData.email,
        password: formData.password,
        ...(captchaToken && { captchaToken })
      };

      const result = await login(loginData.email, loginData.password, loginData.captchaToken, deviceFingerprint);
      
      if (result.success) {
        // Reset attempt count on successful login
        setAttemptCount(0);
        setCaptchaRequired(false);
        setCaptchaToken(null);
        onSuccess && onSuccess(result.user);
      } else {
        // Handle rate limiting response
        if (result.captchaRequired) {
          setCaptchaRequired(true);
          setAttemptCount(result.attempts || attemptCount + 1);
          setError(`Too many failed attempts. Please complete CAPTCHA verification. (Attempt ${result.attempts || attemptCount + 1})`);
          
          // Reset CAPTCHA
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          setCaptchaToken(null);
        } else {
          setAttemptCount(prev => prev + 1);
          setError(result.error);
          
          // Show CAPTCHA after 3 attempts even if server doesn't require it yet
          if (attemptCount >= 2) {
            setCaptchaRequired(true);
          }
        }
      }
    } catch (err) {
      setAttemptCount(prev => prev + 1);
      setError('Login failed. Please try again.');
      
      // Show CAPTCHA after 3 attempts
      if (attemptCount >= 2) {
        setCaptchaRequired(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    if (token) {
      setError(''); // Clear error when CAPTCHA is completed
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  return (
    <Box sx={AUTH_PAGE_BG_SX}>
      <Card sx={{ maxWidth: 400, width: '100%', boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Back Button */}
          {onBack && (
            <Box sx={{ mb: 2 }}>
              <IconButton onClick={onBack} sx={{ color: 'text.secondary' }} aria-label="Back">
                <ArrowBack />
              </IconButton>
            </Box>
          )}

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Psychology sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              JobMockAI
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome back! Sign in to continue your interview preparation journey.
            </Typography>
          </Box>

          <AuthModeTabs mode="login" onLogin={() => {}} onRegister={switchToRegister} />

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              id="login-email"
              name="email"
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              id="login-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={formData.password}
              onChange={handleChange}
              required
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
              }}
            />

            {/* Forgot Password Link */}
            <Box sx={{ textAlign: 'right', mb: 2, mt: -1 }}>
              <Link
                component="button"
                variant="body2"
                type="button"
                onClick={() => navigate('/forgot-password')}
              >
                Forgot your password?
              </Link>
            </Box>

            {/* CAPTCHA Component */}
            {captchaRequired && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Security sx={{ mr: 1, fontSize: 16 }} />
                    Security verification required
                  </Typography>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"} // Test key for development
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    theme="light"
                  />
                  {attemptCount > 0 && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                      Failed attempts: {attemptCount}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || (captchaRequired && !captchaToken)}
              startIcon={<LoginIcon />}
              sx={{
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0a3d8f 30%, #002171 90%)'
                }
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>

            <SocialLoginButtons
              disabled={loading}
              variant="login"
              consentGranted
              consentDocumentIds={regDocuments.map((d) => d.id)}
              docsLoading={docsLoading}
              googleButtonLabel="Continue with"
              legalCaptionVerb="continuing"
              postAuthRedirect={postAuthRedirect}
            />

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                New to JobMockAI?
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<PersonAdd />}
              onClick={switchToRegister}
              sx={{ py: 1.5 }}
            >
              Create Account
            </Button>
          </Box>

          {/* Demo Access */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Want to try without signing up?
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={() => onSuccess && onSuccess(null)} // Continue as guest
              sx={{ mt: 1 }}
            >
              Continue as Guest (Limited Features)
            </Link>
          </Box>

          {/* Features Preview */}
          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              🚀 What you'll get with an account:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              • Unlimited AI-powered interview practice<br/>
              • Progress tracking and performance analytics<br/>
              • Personalized recommendations and coaching<br/>
              • Interview history and session recordings<br/>
              • Advanced STAR method guidance
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;